import { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../../../services/api';
import type { DocumentSelectionMode } from '../../../types/documentSelection';

const DEBOUNCE_MS = 800;
const MIN_QUERY_LENGTH = 3;
const REQUEST_TIMEOUT_MS = 10_000;

export interface DocumentSuggestion {
  id: string;
  name: string;
  // B5: Transparency fields from backend
  reason?: string;
  strategy?: string;
  score?: number;
}

export interface UseDocumentSuggestionsOptions {
  conversationId: string | undefined;
  mode: DocumentSelectionMode;
  inputText: string;
}

export type PreSelectAction = 'keep' | 'replace' | 'merge';

export interface UseDocumentSuggestionsReturn {
  suggestions: DocumentSuggestion[];
  isLoading: boolean;
  clearSuggestions: () => void;
  /** Accept suggestions from the SSE stream (orchestrator auto-select after CQR validation). */
  setStreamSuggestions: (docs: DocumentSuggestion[]) => void;
  confidence: number | null;
  userPromptNeeded: boolean;
  lastAction: PreSelectAction | null;
}

/**
 * Hook that provides debounced document suggestions for Smart mode.
 *
 * Calls POST /chat/pre-select with the user's input text after a debounce period.
 * Only active when mode === 'smart' and input text has >= 3 characters.
 */
export function useDocumentSuggestions({
  conversationId,
  mode,
  inputText,
}: UseDocumentSuggestionsOptions): UseDocumentSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<DocumentSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [userPromptNeeded, setUserPromptNeeded] = useState(false);
  const [lastAction, setLastAction] = useState<PreSelectAction | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef<string>('');
  // Ref to always have current suggestion IDs for the debounced API call (avoids stale closure)
  const suggestionsRef = useRef<DocumentSuggestion[]>(suggestions);
  suggestionsRef.current = suggestions;

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setIsLoading(false);
    lastQueryRef.current = '';
  }, []);

  /**
   * Accept document suggestions from the SSE stream.
   * Called when the orchestrator auto-selects documents (e.g. after CQR
   * validation discards pre-select docs). Always overrides pre-select
   * suggestions since stream suggestions represent what the backend
   * actually used.
   */
  const setStreamSuggestions = useCallback((docs: DocumentSuggestion[]) => {
    setSuggestions(docs);
    setLastAction('replace');
    setConfidence(docs[0]?.score ?? null);
  }, []);

  useEffect(() => {
    // Only active in smart mode
    if (mode !== 'smart') {
      if (suggestions.length > 0) clearSuggestions();
      return;
    }

    const trimmed = inputText.trim();

    // Don't fetch for short input — existing suggestions persist in panel
    // until replaced by a new query, conversation change, or mode switch.
    if (trimmed.length < MIN_QUERY_LENGTH) {
      return;
    }

    // Skip if same query
    if (trimmed === lastQueryRef.current) return;

    // Clear previous debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!conversationId) return;

      // Cancel previous request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Safety timeout: auto-abort if request hangs
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      setIsLoading(true);
      lastQueryRef.current = trimmed;

      try {
        // v2: Send current suggestion IDs for intent classification (use ref to avoid stale closure)
        const currentIds = suggestionsRef.current.map((s) => s.id);
        const result = await apiService.preSelectDocuments(trimmed, conversationId, currentIds);

        clearTimeout(timeoutId);

        // Check if aborted
        if (controller.signal.aborted) return;

        const action: PreSelectAction = result.action || 'replace';
        setLastAction(action);

        if (action === 'keep') {
          // Backend says current docs are still relevant — don't touch suggestions
          setIsLoading(false);
          return;
        }

        if (result && result.document_ids && result.document_ids.length > 0) {
          const newSuggestions: DocumentSuggestion[] = result.document_ids.map(
            (id: string, i: number) => ({
              id,
              name: result.document_names?.[i] || id,
              reason: result.reasons?.[i],
              strategy: result.strategy,
              score: result.confidence,
            })
          );

          if (action === 'merge') {
            // Merge: add new docs that aren't already in suggestions
            setSuggestions((prev) => {
              const existingIds = new Set(prev.map((s) => s.id));
              const toAdd = newSuggestions.filter((s) => !existingIds.has(s.id));
              return [...prev, ...toAdd];
            });
          } else {
            // Replace (default)
            setSuggestions(newSuggestions);
          }
          setConfidence(result.confidence ?? null);
          setUserPromptNeeded(result.user_prompt_needed ?? false);
        } else if (action === 'replace') {
          // Only clear if explicitly replacing with empty results
          setSuggestions([]);
          setConfidence(null);
          setUserPromptNeeded(false);
        }
        // If action is "merge" but no new docs, keep existing (no-op)
      } catch (err) {
        clearTimeout(timeoutId);
        if (!controller.signal.aborted) {
          console.warn('Document suggestion failed:', err);
          setSuggestions([]);
        }
      } finally {
        // Always reset loading — prevents stuck "Buscando..." chip.
        // If aborted by a newer request, that request will set isLoading=true immediately.
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [mode, inputText, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear suggestions when conversation changes (prevents stale cross-conversation data)
  const prevConvRef = useRef(conversationId);
  useEffect(() => {
    if (conversationId !== prevConvRef.current) {
      prevConvRef.current = conversationId;
      clearSuggestions();
    }
  }, [conversationId, clearSuggestions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    suggestions,
    isLoading,
    clearSuggestions,
    setStreamSuggestions,
    confidence,
    userPromptNeeded,
    lastAction,
  };
}
